import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";

export default function AdminFaqs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newQ, setNewQ] = useState({ question: "", answer: "", order: 0, active: true });

  async function load() {
    setLoading(true);
    try { const { data } = await api.get("/faqs"); setItems(data || []); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function create() {
    if (!newQ.question || !newQ.answer) { toast.error("Frage und Antwort erforderlich"); return; }
    try {
      await api.post("/faqs", { ...newQ, order: items.length });
      setNewQ({ question: "", answer: "", order: 0, active: true });
      toast.success("FAQ angelegt");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function update(it) {
    try { await api.put(`/faqs/${it.id}`, { question: it.question, answer: it.answer, order: it.order, active: it.active }); toast.success("Gespeichert"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }
  async function del(id) {
    if (!window.confirm("FAQ wirklich löschen?")) return;
    try { await api.delete(`/faqs/${id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  return (
    <div data-testid="admin-faqs">
      <h1 className="font-semibold text-3xl mb-1">FAQs</h1>
      <p className="text-sm text-neutral-500 mb-10">Häufige Fragen zum Quartier verwalten.</p>

      <div className="bg-white border border-brand-line p-6 mb-10">
        <h2 className="font-semibold text-lg mb-4">Neue Frage hinzufügen</h2>
        <div className="space-y-3">
          <input
            placeholder="Frage"
            value={newQ.question}
            onChange={(e) => setNewQ({ ...newQ, question: e.target.value })}
            data-testid="faq-new-question"
            className="w-full border border-brand-line px-3 py-2.5 text-sm focus:outline-none focus:border-[hsl(var(--brand-green-strong))]"
          />
          <RichTextEditor
            value={newQ.answer}
            onChange={(html) => setNewQ({ ...newQ, answer: html })}
            minHeight={120}
            placeholder="Antwort eingeben…"
            testid="faq-new-answer"
          />
          <button onClick={create} data-testid="faq-new-submit" className="inline-flex items-center gap-2 bg-[hsl(var(--brand-green-dark))] text-white px-5 py-2.5 text-sm hover:bg-[hsl(var(--brand-green-strong))]">
            <Plus className="h-4 w-4" /> Hinzufügen
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-neutral-500">Lade…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-neutral-500">Noch keine FAQs angelegt.</p>
      ) : (
        <div className="space-y-4">
          {items.map((it, idx) => (
            <FaqRow key={it.id} item={it} index={idx} onSave={update} onDelete={del} />
          ))}
        </div>
      )}
    </div>
  );
}

function FaqRow({ item, index, onSave, onDelete }) {
  const [it, setIt] = useState(item);
  useEffect(() => setIt(item), [item]);
  return (
    <div className="bg-white border border-brand-line p-5 space-y-3" data-testid={`faq-row-${item.id}`}>
      <div className="flex items-center gap-3">
        <span className="text-xs text-neutral-400 w-8">#{index + 1}</span>
        <input value={it.question} onChange={(e) => setIt({ ...it, question: e.target.value })} className="flex-1 border border-brand-line px-3 py-2 text-sm" />
        <label className="flex items-center gap-1.5 text-xs text-neutral-600">
          <input type="checkbox" checked={it.active} onChange={(e) => setIt({ ...it, active: e.target.checked })} /> Aktiv
        </label>
        <input type="number" value={it.order} onChange={(e) => setIt({ ...it, order: parseInt(e.target.value) || 0 })} className="w-16 border border-brand-line px-2 py-2 text-sm" />
      </div>
      <RichTextEditor
        value={it.answer}
        onChange={(html) => setIt({ ...it, answer: html })}
        minHeight={100}
        placeholder="Antwort…"
        testid={`faq-row-answer-${item.id}`}
      />
      <div className="flex justify-end gap-2">
        <button onClick={() => onDelete(it.id)} className="text-xs text-neutral-500 hover:text-red-600 inline-flex items-center gap-1"><Trash2 className="h-3.5 w-3.5" /> Löschen</button>
        <button onClick={() => onSave(it)} className="inline-flex items-center gap-2 border border-brand-line px-3 py-1.5 text-xs hover:bg-neutral-50"><Save className="h-3.5 w-3.5" /> Speichern</button>
      </div>
    </div>
  );
}
